import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  productService,
  Product,
  getLocalizedString,
} from "../../services/firebaseService";
import DetailLayout from "../DetailLayout/DetailLayout";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentLang = i18n.language || "en";

  useEffect(() => {
    if (!id) {
      navigate("/products");
      return;
    }

    const loadProduct = async () => {
      try {
        setLoading(true);
        const productData = await productService.getProduct(id);
        if (productData) {
          setProduct(productData);
          setError(null);
        } else {
          setError("Product not found");
        }
      } catch (err) {
        console.error("Error loading product:", err);
        setError("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, navigate]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    const loadProduct = async () => {
      try {
        const productData = await productService.getProduct(id!);
        if (productData) {
          setProduct(productData);
        } else {
          setError("Product not found");
        }
      } catch (err) {
        setError("Failed to load product");
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  };

  return (
    <DetailLayout
      loading={loading}
      error={error}
      notFoundTitle="Product Not Found"
      // notFoundMessage="The product you are looking for does not exist."
      backPath="/products"
      backLabel={t("common.backToProducts")}
      onRetry={handleRetry}
    >
      {product &&
        (() => {
          const localizedTitle = getLocalizedString(product.title, currentLang);
          // const localizedShortDescription = getLocalizedString(
          //   product.shortDescription,
          //   currentLang
          // );
          const localizedDescription = getLocalizedString(
            product.description,
            currentLang
          );

          return (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 mb-12 items-start md:gap-8">
                <div className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-[var(--background-secondary)] group md:aspect-[16/9]">
                  <img
                    src={product.image}
                    alt={localizedTitle}
                    className="w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/images/placeholder.png";
                    }}
                  />
                </div>
                <div className="flex flex-col gap-4">
                  <h1 className="text-[2.5rem] font-extrabold text-[var(--text-primary)] leading-tight m-0 md:text-[2rem]">
                    {localizedTitle}
                  </h1>
                  {/* <h2 className="text-xl text-[var(--text-secondary)] leading-normal m-0 md:text-lg">
                    {localizedShortDescription}
                  </h2> */}
                  <div className="flex flex-wrap gap-4 items-center mt-4 md:flex-col md:items-start md:gap-3">
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--background-secondary)] rounded-full text-lg font-bold text-primary">
                      {product.price}
                    </span>
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--gradient-secondary)]  rounded-full text-sm font-medium text-[var(--text-secondary)]">
                      {product.inStock
                        ? `In Stock (${product.stockQuantity})`
                        : "Out of Stock"}
                    </span>
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--gradient-primary)]  rounded-full text-sm font-medium text-[var(--text-secondary)]">
                      {product.category}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid gap-8">
                <div className="bg-[var(--background-primary)] border border-[var(--border-color)] rounded-xl p-8 shadow-[0_2px_4px_var(--shadow-color)] md:p-6">
                  {/* <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2 md:text-xl">
                    Description
                  </h3> */}
                  {/* <p className="text-[var(--text-secondary)] leading-relaxed mb-0">
                    {localizedDescription}
                  </p> */}
                  <div
                    className="text-[var(--text-secondary)] leading-relaxed [&_h1]:text-[var(--text-primary)] [&_h1]:mt-8 [&_h1]:mb-4 [&_h2]:text-[var(--text-primary)] [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:text-[var(--text-primary)] [&_h3]:mt-8 [&_h3]:mb-4 [&_h4]:text-[var(--text-primary)] [&_h4]:mt-8 [&_h4]:mb-4 [&_h5]:text-[var(--text-primary)] [&_h5]:mt-8 [&_h5]:mb-4 [&_h6]:text-[var(--text-primary)] [&_h6]:mt-8 [&_h6]:mb-4 [&_h1:first-child]:mt-0 [&_h2:first-child]:mt-0 [&_h3:first-child]:mt-0 [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:pl-8 [&_ol]:mb-4 [&_ol]:pl-8 [&_li]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:my-6 [&_blockquote]:italic [&_blockquote]:text-[var(--text-secondary)] [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-4"
                    dangerouslySetInnerHTML={{ __html: localizedDescription }}
                  />
                </div>

                {product.features && product.features.length > 0 && (
                  <div className="bg-[var(--background-primary)] border border-[var(--border-color)] rounded-xl p-8 shadow-[0_2px_4px_var(--shadow-color)] md:p-6">
                    <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2 md:text-xl">
                      Features
                    </h3>
                    <ul className="list-none p-0 m-0">
                      {product.features.map((feature, index) => (
                        <li
                          key={index}
                          className="py-3 border-b border-[var(--border-color)] text-[var(--text-secondary)] relative pl-8 last:border-b-0 before:content-['✓'] before:absolute before:left-0 before:text-primary before:font-bold"
                        >
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {product.specifications &&
                  Object.keys(product.specifications).length > 0 && (
                    <div className="bg-[var(--background-primary)] border border-[var(--border-color)] rounded-xl p-8 shadow-[0_2px_4px_var(--shadow-color)] md:p-6">
                      <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2 md:text-xl">
                        Specifications
                      </h3>
                      <div className="grid gap-3">
                        {Object.entries(product.specifications).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="grid grid-cols-[1fr_2fr] gap-4 p-3 bg-[var(--background-secondary)] rounded-lg md:grid-cols-1 md:gap-2"
                            >
                              <span className="font-semibold text-[var(--text-primary)]">
                                {key}:
                              </span>
                              <span className="text-[var(--text-secondary)]">
                                {value}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                <div className="bg-[var(--gradient-primary)] text-center py-12 px-8 rounded-xl mt-8">
                  <h3 className="text-2xl font-bold  mb-4">
                    Ready to Purchase?
                  </h3>
                  <p className=" mb-8 text-center text-[var(--text-secondary)]">
                    Contact us to learn more about this product and how to place
                    an order.
                  </p>
                  <div className="flex gap-4 justify-center flex-wrap md:flex-col md:items-center">
                    <button
                      className="  border-none px-8 py-4 rounded-lg font-semibold cursor-pointer transition-all duration-200 hover:bg-[var(--background-secondary)] hover:-translate-y-0.5 hover:shadow-lg md:w-full md:max-w-[300px]"
                      onClick={() => navigate("/contact")}
                    >
                      Contact Us
                    </button>
                    <button
                      className="bg-transparent  border-2 border-white px-8 py-4 rounded-lg font-semibold cursor-pointer transition-all duration-200 hover:bg-white hover:text-primary md:w-full md:max-w-[300px]"
                      onClick={() => navigate("/products")}
                    >
                      View All Products
                    </button>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
    </DetailLayout>
  );
}
